/**
 * ALN Sync table — column order and headers match Angular `import-data.component.html` (alnsync tab).
 */
import type { ImportDataType } from '../types/importData'

export const ALN_SYNC_ACTION_KEY = '__action__' as const

import type { AlnSyncColumn } from '../types'

export type { AlnSyncColumn } from '../types'

const MANAGEMENT_COMPANY: AlnSyncColumn[] = [
  { key: 'property_name', label: 'Property Name' },
  { key: 'team_name', label: 'Team' },
  { key: 'listed', label: 'Status' },
  { key: 'previous_mgmt', label: 'Old Company Name' },
  { key: 'current_mgmt', label: 'New Company Name' },
  { key: 'uploaded_at', label: 'Upload Date' },
  { key: ALN_SYNC_ACTION_KEY, label: 'Action' },
]

const PROPERTY_NAME: AlnSyncColumn[] = [
  { key: 'fk_property_id', label: 'Property ID' },
  { key: 'listed', label: 'Status' },
  { key: 'previous_property_name', label: 'Old Property Name' },
  { key: 'team_name', label: 'Team' },
  { key: 'current_property_name', label: 'New Property Name' },
  { key: 'uploaded_at', label: 'Upload Date' },
]

const MANAGER_CHANGE: AlnSyncColumn[] = [
  { key: 'property_name', label: 'Property Name' },
  { key: 'team_name', label: 'Team' },
  { key: 'mgmt_company', label: 'Management Company' },
  { key: 'current_manager_email', label: 'Email' },
  { key: 'previous_manager_name', label: 'Old Manager' },
  { key: 'current_manager_name', label: 'New Manager' },
  { key: 'uploaded_at', label: 'Upload Date' },
  { key: ALN_SYNC_ACTION_KEY, label: 'Action' },
]

const AREA_SUPERVISOR: AlnSyncColumn[] = [
  { key: 'property_name', label: 'Property Name' },
  { key: 'team_name', label: 'Team' },
  { key: 'mgmt_company', label: 'Management Company' },
  { key: 'previous_area_supervisor_email', label: 'Old Email' },
  { key: 'current_area_supervisor_email', label: 'New Email' },
  { key: 'previous_area_supervisor_name', label: 'Previous A/S' },
  { key: 'current_area_supervisor_name', label: 'Current A/S' },
  { key: 'uploaded_at', label: 'Upload Date' },
  { key: ALN_SYNC_ACTION_KEY, label: 'Action' },
]

const PMC_IMPORT: AlnSyncColumn[] = [
  { key: 'current_pmc_name', label: 'Management Company' },
  { key: 'team_name', label: 'Team' },
  { key: 'current_pmc_address_1', label: 'Address' },
  { key: 'current_pmc_city', label: 'City' },
  { key: 'current_pmc_state', label: 'State' },
  { key: 'current_pmc_phone', label: 'Phone' },
  { key: 'uploaded_at', label: 'Upload Date' },
  { key: ALN_SYNC_ACTION_KEY, label: 'Action' },
]

const PROPERTY_IMPORT: AlnSyncColumn[] = [
  { key: 'current_property_name', label: 'Property Name' },
  { key: 'team_name', label: 'Team' },
  { key: 'current_property_address', label: 'Address' },
  { key: 'current_property_city', label: 'City' },
  { key: 'current_property_state', label: 'State' },
  { key: 'current_property_phone', label: 'Phone' },
  { key: 'uploaded_at', label: 'Upload Date' },
  { key: ALN_SYNC_ACTION_KEY, label: 'Action' },
]

const OCCUPANCY: AlnSyncColumn[] = [
  { key: 'current_property_name', label: 'Property Name' },
  { key: 'team_name', label: 'Team' },
  { key: 'previous_occupancy', label: 'Previous Occupancy' },
  { key: 'current_occupancy', label: 'Current Occupancy' },
  { key: 'uploaded_at', label: 'Upload Date' },
]

const BY_TYPE: Partial<Record<ImportDataType, AlnSyncColumn[]>> = {
  management_company: MANAGEMENT_COMPANY,
  property_name: PROPERTY_NAME,
  manager_change: MANAGER_CHANGE,
  area_supervisor: AREA_SUPERVISOR,
  pmc_import: PMC_IMPORT,
  property_import: PROPERTY_IMPORT,
  occupancy: OCCUPANCY,
}

export function getAlnSyncColumns(importType: ImportDataType): AlnSyncColumn[] {
  if (!importType) return []
  return BY_TYPE[importType] ?? []
}

const HISTORY_EXTRA_COLS: AlnSyncColumn[] = [
  { key: 'approved_at', label: 'Sync Date' },
  { key: 'undone_status', label: 'Undone' },
  { key: 'approved_by', label: 'Approved By' },
]

export function getAlnHistoryColumns(importType: ImportDataType): AlnSyncColumn[] {
  if (!importType) return []
  const base = BY_TYPE[importType] ?? []
  if (!base.length) return []

  const hasAction = base.some((c) => c.key === ALN_SYNC_ACTION_KEY)
  const nonAction = base.filter((c) => c.key !== ALN_SYNC_ACTION_KEY)

  // Label override for Upload Date in management_company history (Angular: Upload At)
  const adjustedNonAction = nonAction.map((c) =>
    importType === 'management_company' && c.key === 'uploaded_at' ? { ...c, label: 'Upload At' } : c,
  )

  return hasAction
    ? [...adjustedNonAction, ...HISTORY_EXTRA_COLS, { key: ALN_SYNC_ACTION_KEY, label: 'Action' }]
    : [...adjustedNonAction, ...HISTORY_EXTRA_COLS]
}

const UPLOAD_BY_TYPE: Partial<Record<ImportDataType, AlnSyncColumn[]>> = {
  management_company: [
    { key: 'property_name', label: 'Property Name' },
    { key: 'previous_mgmt', label: 'Old Company Name' },
    { key: 'current_mgmt', label: 'New Company Name' },
    { key: ALN_SYNC_ACTION_KEY, label: 'Action' },
  ],
  property_name: [
    { key: 'fk_property_id', label: 'Property ID' },
    { key: 'previous_property_name', label: 'Old Property Name' },
    { key: 'current_property_name', label: 'New Property Name' },
  ],
  manager_change: [
    { key: 'property_name', label: 'Property Name' },
    { key: 'mgmt_company', label: 'Management Company' },
    { key: 'current_manager_email', label: 'Email' },
    { key: 'previous_manager_name', label: 'Old Manager' },
    { key: 'current_manager_name', label: 'New Manager' },
    { key: ALN_SYNC_ACTION_KEY, label: 'Action' },
  ],
  area_supervisor: [
    { key: 'property_name', label: 'Property Name' },
    { key: 'mgmt_company', label: 'Management Company' },
    { key: 'previous_area_supervisor_name', label: 'Previous A/S' },
    { key: 'current_area_supervisor_name', label: 'Current A/S' },
    { key: 'current_area_supervisor_email', label: 'Email' },
    { key: ALN_SYNC_ACTION_KEY, label: 'Action' },
  ],
  pmc_import: [
    { key: 'current_pmc_name', label: 'Management Company' },
    { key: 'current_pmc_address_1', label: 'Address' },
    { key: 'current_pmc_city', label: 'City' },
    { key: 'current_pmc_state', label: 'State' },
    { key: 'current_pmc_phone', label: 'Phone' },
    { key: ALN_SYNC_ACTION_KEY, label: 'Action' },
  ],
  property_import: [
    { key: 'current_property_name', label: 'Property Name' },
    { key: 'current_property_address', label: 'Address' },
    { key: 'current_property_city', label: 'City' },
    { key: 'current_property_state', label: 'State' },
    { key: 'current_property_phone', label: 'Phone' },
    { key: ALN_SYNC_ACTION_KEY, label: 'Action' },
  ],
  occupancy: [
    { key: 'current_property_name', label: 'Property Name' },
    { key: 'previous_occupancy', label: 'Previous Occupancy' },
    { key: 'current_occupancy', label: 'Current Occupancy' },
  ],
}

export function getAlnUploadColumns(importType: ImportDataType): AlnSyncColumn[] {
  if (!importType) return []
  return UPLOAD_BY_TYPE[importType] ?? []
}

export function listedToStatus(listed: unknown): string {
  return listed === '1' || listed === 1 ? 'Active' : 'Not Active'
}
